import React, { createContext, useContext, useEffect, useState } from 'react'
import { z } from 'zod'
import type { Tenant } from '@/types'
import { api } from '@/lib/api'
import { readStoredTenantId } from '@/lib/tenant'
import { tenantConfigSchema, tenantSchema } from '@/lib/contracts/common'

interface TenantConfig {
  featureFlags: Record<string, boolean>
  branding?: {
    logoUrl?: string
    primaryColor?: string
    name?: string
  }
  locale: string
  timezone: string
  currency: string
}

interface TenantContextValue {
  tenant: Tenant | null
  config: TenantConfig
  isLoading: boolean
}

const defaultConfig: TenantConfig = {
  featureFlags: {},
  locale: 'en-US',
  timezone: 'UTC',
  currency: 'USD',
}

const TenantContext = createContext<TenantContextValue>({
  tenant: null,
  config: defaultConfig,
  isLoading: false,
})

/** Bootstrap payload for the active tenant (validated at the boundary). */
const tenantBootstrapSchema = z.object({
  tenant: tenantSchema.optional(),
  config: tenantConfigSchema.partial().optional(),
})

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [config, setConfig] = useState<TenantConfig>(defaultConfig)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    // Tenant identity is read once, through the tenant guard — never ad hoc.
    const tenantId = readStoredTenantId()
    if (!tenantId) return

    let cancelled = false
    setIsLoading(true)

    api
      .get<z.infer<typeof tenantBootstrapSchema>>('/api/v1/auth/me', undefined, {
        tenantId,
        schema: tenantBootstrapSchema,
      })
      .then((data) => {
        if (cancelled) return
        if (data.tenant) setTenant(data.tenant as Tenant)
        if (data.config) setConfig({ ...defaultConfig, ...data.config })
      })
      .catch(() => {
        // Non-blocking: tenant config failure must not break the app.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  return (
    <TenantContext.Provider value={{ tenant, config, isLoading }}>
      {children}
    </TenantContext.Provider>
  )
}

export function useTenant(): TenantContextValue {
  return useContext(TenantContext)
}

